
import React, { useEffect, useState, Fragment } from "react";
import { SearchIcon } from "@heroicons/react/solid";
import { EyeIcon, XIcon, ClockIcon, CheckCircleIcon } from "@heroicons/react/outline";
import Layout from "../../components/Layout/Layout";
import APICall from "../../utils/APICall";
import QuickOnlineAccountServices, {
  ONLINE_ACCOUNT_APP_NAME,
  PREMIUM_MOBILE_APP_NAME,
} from "../../services/QuickOnlineAccountServices";
import Paginate from "../../utils/Paginate";
import { convert, formatDate } from "../../utils/convertDate";
import DatePicker from "react-date-picker";
import { Dialog, Transition } from "@headlessui/react";

const durationStyles = {
  fast: "bg-green-100 text-green-800",
  medium: "bg-yellow-100 text-yellow-800",
  slow: "bg-red-100 text-red-800",
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function LivenessImages() {
  const [images, setImages] = useState([]);
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [prevPage, setPrev] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPage, setPage] = useState(0);
  const [nextPage, setNextPage] = useState(false);
  const oldFrom = new Date().setDate(new Date().getDate() - 7);
  const [starting, setStarting] = useState(convert(formatDate(new Date(oldFrom))));
  const [ending, setEnding] = useState(convert(formatDate(new Date())));
  const [formValues, setFormValues] = useState({
    from: new Date(oldFrom),
    to: new Date(),
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [appName, setAppName] = useState(ONLINE_ACCOUNT_APP_NAME);

  const listLiveness = async (searchParam, fromParam, toParam, customerId) => {
    const tenantId = sessionStorage.getItem("tenantId");
    const urlParams = `?appName=${appName}&tenantId=${tenantId}&page=${pageIndex}&pageLimit=${10}`;
    let url = `/api/QuickOnlineAccountOpening/admin/list/liveness-images${urlParams}`;
    if (fromParam) url += `&startDate=${fromParam}`;
    if (toParam) url += `&endDate=${toParam}`;
    if (searchParam) url += `&search=${searchParam}`;
    if (customerId) url += `&customerId=${customerId}`;
    const resp = await APICall(url, "GET");
    if (resp && resp.data) {
      setImages([...resp.data.result]);
      setPageNumber(resp.data.totalCount);
      setPageIndex(resp.data.pageIndex);
      setPrev(resp.data.hasPreviousPage);
      setPage(resp.data.totalPages);
      setNextPage(resp.data.hasNextPage);
    }
  };

  const range = async (e) => {
    e.preventDefault();
    var longDate = formatDate(formValues.from);
    setStarting(convert(longDate));

    const toDate = formatDate(formValues.to);
    setEnding(convert(toDate));

    listLiveness(search, convert(longDate), convert(toDate));
  };

  const dateValidation = {
    fromHandler: (event) => {
      setFormValues({ ...formValues, from: event });
    },
    toHandler: (event) => {
      setFormValues({ ...formValues, to: event });
    },
  };

  useEffect(() => {
    listLiveness(search, starting, ending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, appName]);

  const viewImage = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const getDurationStyle = (ms) => {
    if (!ms) return "bg-gray-100 text-gray-800";
    if (ms < 5000) return durationStyles.fast;
    if (ms < 15000) return durationStyles.medium;
    return durationStyles.slow;
  };

  return (
    <>
      <Layout title="Liveness Images" pageTag={"account"} current={"/online/accounts/liveness-images"}>
        <React.Fragment>
          <main>
            <div className="max-w-7xl mx-auto">
              <div className="">
                <div className="sm:flex sm:items-center">
                  <div className="sm:flex-auto flex">
                    <div className="relative rounded-md shadow-sm ml-3">
                      <p className="text-sm leading-3 text-gray-500">App Name</p>
                      <select
                        value={appName}
                        onChange={(e) => {
                          setAppName(e.target.value);
                          setPageIndex(1);
                        }}
                        className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                      >
                        <option value={ONLINE_ACCOUNT_APP_NAME}>Online Account</option>
                        <option value={PREMIUM_MOBILE_APP_NAME}>Premium Mobile</option>
                      </select>
                    </div>
                    <div className="relative rounded-md shadow-sm ml-3">
                      <p className="text-sm leading-3 text-gray-500">Start Date</p>
                      <DatePicker onChange={dateValidation.fromHandler} value={formValues.from} />
                    </div>
                    <div className="relative rounded-md shadow-sm ml-3">
                      <p className="text-sm leading-3 text-gray-500">End Date</p>
                      <DatePicker onChange={dateValidation.toHandler} value={formValues.to} />
                    </div>
                    <div className="mt-5 ml-4 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        type="text"
                        name="search"
                        id="search"
                        onChange={(e) => setSearch(e.target.value)}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                        placeholder="Search"
                      />
                    </div>
                    <div className="mt-5 ml-3">
                      <button
                        onClick={range}
                        className="hover:text-white active:text-white inline-flex mr-2 items-center justify-center rounded-md border border-transparent btn-green px-6 py-2 text-sm font-medium text-green shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </div>

                {images.length === 0 ? (
                  <div className="mt-8">No data available for these dates to show.</div>
                ) : (
                  <>
                    <div className="mt-8 flex">
                      <div className="-my-2 overflow-x-auto">
                        <div className="inline-block min-w-full py-2 align-middle">
                          <div className="relative ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full table-fixed divide-y divide-gray-300">
                              <thead>
                                <tr>
                                  <th scope="col" className="min-w-[8rem] py-3.5 pr-3 italic font-normal text-left text-sm text-gray-500">
                                    Self Image
                                  </th>
                                  <th scope="col" className="min-w-[8rem] py-3.5 pr-3 italic font-normal text-left text-sm text-gray-500">
                                    Session ID
                                  </th>
                                  <th scope="col" className="min-w-[8rem] py-3.5 text-left text-sm italic font-normal text-gray-500">
                                    Liveness Duration
                                  </th>
                                  <th scope="col" className="min-w-[6rem] px-3 py-3.5 text-left text-sm italic font-normal text-gray-500">
                                    Challenges
                                  </th>
                                  <th scope="col" className="min-w-[8rem] px-3 py-3.5 text-left text-sm italic font-normal text-gray-500">
                                    Date Created
                                  </th>
                                  <th scope="col" className="px-3 py-3.5 text-left text-sm italic font-normal text-gray-900">
                                    <span className="sr-only">Edit</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 bg-white">
                                {images.map((item, i) => (
                                  <tr key={i} className="bg-gray-50">
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                      {item.base64Image ? (
                                        <div
                                          className="relative h-12 w-12 rounded-full overflow-hidden border border-rose-600 cursor-pointer"
                                          onClick={() => viewImage(item)}
                                        >
                                          <img
                                            src={item.base64Image}
                                            alt="BVN Image"
                                            className="h-12 w-12 rounded-full object-cover"
                                          />
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">N/A</span>
                                      )}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                      {item.sessionId || "N/A"}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                                      <span className={classNames(
                                        getDurationStyle(item.livenessDurationMs),
                                        "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium",
                                      )}>
                                        {item.livenessDurationMs != null
                                          ? `${(item.livenessDurationMs / 1000).toFixed(2)}s`
                                          : "N/A"}
                                      </span>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                      <span className="inline-flex items-center text-gray-500">
                                        <CheckCircleIcon className="h-4 w-4 mr-1.5 text-green-500" />
                                        {item.listOfCompletedChallenges
                                          ? item.listOfCompletedChallenges.length
                                          : 0}
                                      </span>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                      {formatDate(item.createdOn)}
                                    </td>
                                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                      <button
                                        onClick={() => viewImage(item)}
                                        className="inline-flex items-center text-indigo-600 hover:text-indigo-900"
                                      >
                                        <EyeIcon className="h-4 w-4 mr-1" />
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Paginate
                      nextPage={nextPage}
                      pageIndex={pageIndex}
                      totalCount={pageNumber}
                      totalPages={totalPage}
                      prevPage={prevPage}
                      setPageIndex={setPageIndex}
                    />
                  </>
                )}
              </div>
            </div>
          </main>
        </React.Fragment>
      </Layout>

      <Transition.Root show={showImageModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowImageModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-lg bg-white rounded-lg px-6 py-6 text-left shadow-xl overflow-hidden">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      Liveness Image Detail
                    </Dialog.Title>
                    <button
                      onClick={() => setShowImageModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XIcon className="h-6 w-6" />
                    </button>
                  </div>
                  {selectedImage && (
                    <div className="space-y-4">
                      {selectedImage.selfImageUrl && (
                        <div className="flex justify-center">
                          <img
                            src={selectedImage.selfImageUrl}
                            alt="Selfie"
                            className="h-64 w-64 object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Session ID</p>
                          <p className="text-sm font-medium text-gray-900 break-all">
                            {selectedImage.sessionId || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">App Name</p>
                          <p className="text-sm font-medium text-gray-900">{selectedImage.appName || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Liveness Duration</p>
                          <p className="text-sm font-medium text-gray-900 flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                            {selectedImage.livenessDurationMs != null
                              ? `${(selectedImage.livenessDurationMs / 1000).toFixed(2)}s`
                              : "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Date Created</p>
                          <p className="text-sm font-medium text-gray-900">{formatDate(selectedImage.createdOn)}</p>
                        </div>
                      </div>
                      {selectedImage.completedChallengesImages &&
                        selectedImage.completedChallengesImages.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Completed Challenge Images
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                              {selectedImage.completedChallengesImages.map((img, idx) => (
                                <div key={idx} className="text-center">
                                  <img
                                    src={img.imageUrl}
                                    alt={img.challenge}
                                    className="h-20 w-20 object-cover rounded-lg border border-gray-200 mx-auto"
                                  />
                                  <p className="mt-1 text-[10px] text-gray-500 truncate">{img.challenge}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      {selectedImage.listOfCompletedChallenges &&
                        selectedImage.listOfCompletedChallenges.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-900 mb-2">Completed Challenges</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedImage.listOfCompletedChallenges.map((ch, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                >
                                  <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                                  {ch}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
